import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Avatar,
  Badge,
  Box,
  Center,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
  Skeleton,
  Stack,
  Td,
  Text,
  Tooltip,
  Tr,
  VStack,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';
import { USD } from '@dinero.js/currencies';
import { dinero } from 'dinero.js';
import truncate from 'lodash/truncate';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { AccordionMembershipCard } from '@/components/AccordionMembershipCard';
import { AccountStatus } from '@/components/AccountStatus';
import { MembershipEmptyCard } from '@/components/MembershipEmptyCard';
import { MostRelevantMembershipTermAlertTag } from '@/components/MostRelevantMembershipTermAlertTag';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { Role } from '@/components/Role';
import { CompanyInfoEdit } from '@/components/admin/sponsorships/Sponsors/components/Company/components/CompanyInfoEdit';
import { CompanyInfoEdit_OrganizationFragment } from '@/components/admin/sponsorships/Sponsors/components/Company/components/CompanyInfoEdit/graphql-types/CompanyInfoEdit_OrganizationFragment';
import { EPrepImagesSource } from '@/graphql-types/globalTypes';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { useMembershipDisplayConfig } from '@/hooks/useMembershipDisplayConfig';
import { Card } from '@/imports/ui/chakra/components/Card';
import { BrifcaseIcon } from '@/imports/ui/chakra/customIcon/BrifcaseIcon';
import { EmailIcon } from '@/imports/ui/chakra/customIcon/EmailIcon';
import { GlobeIcon } from '@/imports/ui/chakra/customIcon/GlobeIcon';
import { MemberIcon } from '@/imports/ui/chakra/customIcon/MemberIcon';
import { PhoneNoIcon } from '@/imports/ui/chakra/customIcon/PhoneNoIcon';
import { OrganiizationIcon } from '@/imports/ui/chakra/customIcon/oorganization';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EditIcon,
  EyeIcon,
  MapPinIcon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
} from '@/imports/ui/chakra/feather';
import { BannerImageMenu } from '@/imports/ui/components/BannerImageMenu';
import { ProfileImageMenu } from '@/imports/ui/components/ProfileImageMenu';
import {
  getDisplayMembershipStatus,
  MEMBERSHIP_STATUS_COLOR,
  READABLE_MEMBERSHIP_STATUS,
} from '@/utilities';
import { getOrganizationAdminBasePath } from '@/utilities/archivedOrganizationPaths';

import { EditMemberModal } from '../EditMember/EditMemberModal';
import { USER as MEMBER_USER_QUERY } from '../Member/Member';
import {
  Member_User_Query,
  Member_User_QueryVariables,
} from '../Member/graphql-types/Member_User_Query';
import {
  ORGANIZATION_DETAIL_QUERY,
  organizationListFragment,
} from '../OrganizationRow/OrganizationRow';

const UPDATE_ORGANIZATION_IMAGE = gql`
  mutation CombinedCompanyDetailsRow_UpdateOrganization(
    $input: UpdateOrganizationV2Input!
  ) {
    updateOrganizationV2(input: $input) {
      ... on UpdateOrganizationV2Success {
        organization {
          _id
          ...CompanyDetailsRow_OrganizationFragment
          bannerImage {
            _id
            imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
          }
        }
      }
    }
  }
  ${organizationListFragment}
`;

type CombinedSearchRowProps = {
  type: 'member' | 'organization';
  data: any;
  isExpanded: boolean;
  onToggleExpand: () => void;
  archivedOnly?: boolean;
};

export const CombinedSearchRow: React.FC<CombinedSearchRowProps> = ({
  type,
  data,
  isExpanded,
  onToggleExpand,
  archivedOnly = false,
}) => {
  const router = useRouter();
  const memberManagement = useMemberManagement();
  // When on, remap Ex-Member → Non-Member in Membership Status column
  const { showActiveAndNonMembersOnly, loading: displayConfigLoading } =
    useMembershipDisplayConfig();
  const { currency } = useDefaultCurrency();

  const isMember = type === 'member';
  const isOrganization = type === 'organization';

  // Modal states
  const memberEditModal = useDisclosure();
  const orgEditModal = useDisclosure();
  const [isUpdatingImage, setIsUpdatingImage] = useBoolean();

  // Organization image upload hooks
  const { uploadImages: uploadBannerImages } = useImageUpload(
    EPrepImagesSource.SPONSOR_BANNER_IMAGE
  );
  const { uploadImages: uploadProfilePicture } = useImageUpload(
    EPrepImagesSource.SPONSOR_PROFILE_PICTURE
  );

  const [updateOrganizationImage] = useMutation(UPDATE_ORGANIZATION_IMAGE);

  // Organization attributes processing (moved outside renderExpandedContent)
  const organizationAttributes = React.useMemo(() => {
    if (!isOrganization) return null;

    const results = data?.profileAttributes?.results ?? [];

    const getValue = (key: string) => {
      const found = results.find(({ attribute }: any) => {
        const label = attribute?.label?.toLowerCase?.() ?? '';
        const slug = attribute?.slug?.toLowerCase?.() ?? '';
        return label === key || slug === key;
      });

      const attribute = found?.attribute;
      if (!attribute) return '-';

      return (() => {
        switch (attribute.__typename) {
          case 'FreeTextAttribute':
            return attribute.freeTextValue ?? '—';
          default:
            return '—';
        }
      })();
    };

    return {
      email: getValue('email'),
      phone: getValue('primary phone'),
      website: getValue('website'),
      location: [getValue('city'), getValue('state')]
        .filter(Boolean)
        .join(', '),
    };
  }, [isOrganization, data?.profileAttributes?.results]);

  const memberDetailQuery = useQuery<
    Member_User_Query,
    Member_User_QueryVariables
  >(MEMBER_USER_QUERY, {
    variables: {
      userInput: { id: data._id },
      relevantMembershipTermsInput: { includeRecentlyLapsed: true },
    },
    skip: !isMember || !isExpanded,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const orgDetailQuery = useQuery(ORGANIZATION_DETAIL_QUERY, {
    variables: {
      input: { id: data._id },
      relevantMembershipTermsInput: { includeRecentlyLapsed: true },
    },
    skip: !isOrganization || !isExpanded,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const memberDetailUser = memberDetailQuery.data?.user?.result;
  const memberMembershipData = memberDetailUser?.membershipData;
  const postCount = memberDetailUser?.postCount ?? 0;
  const likeCount = memberDetailUser?.likeCount ?? 0;
  const commentCount = memberDetailUser?.commentCount ?? 0;
  const badgeCount = memberDetailUser?.badgeCount ?? 0;
  const isLoadingMemberDetail =
    memberDetailQuery.loading && !memberDetailUser;

  const orgDetailResult = isOrganization
    ? orgDetailQuery.data?.organization?.result
    : null;
  const orgMembershipData = orgDetailResult?.membershipData;
  const orgBannerImage = orgDetailResult?.bannerImage;
  const isLoadingOrgDetail = orgDetailQuery.loading && !orgDetailResult;

  // Organization image update function
  const getUpdate = (
    type: 'banner' | 'profile'
  ): ((file: any) => Promise<any>) => {
    return async (file: any): Promise<any> => {
      const isBannerImage = type === 'banner';
      setIsUpdatingImage.on();
      if (file === null) {
        await updateOrganizationImage({
          variables: {
            input: {
              id: data._id,
              data: isBannerImage
                ? { sponsorBannerImageId: null }
                : { sponsorProfilePictureId: null },
            },
          },
        });
        setIsUpdatingImage.off();
        return;
      }
      const resource = isBannerImage
        ? await uploadBannerImages([file])
        : await uploadProfilePicture([file]);
      if (resource) {
        await updateOrganizationImage({
          variables: {
            input: {
              id: data._id,
              data: isBannerImage
                ? { sponsorBannerImageId: resource[0] }
                : { sponsorProfilePictureId: resource[0] },
            },
          },
        });
      }
      setIsUpdatingImage.off();
    };
  };

  // Member-specific data processing
  const memberTruncatedData = isMember
    ? {
        displayName: truncate(data.displayName, { length: 35 }),
        email: truncate(data.email ?? '', { length: 30 }),
        company: truncate(data.company ?? '', { length: 30 }),
        membershipTypeName: truncate(
          data.membershipData?.currentMembershipTerm?.membershipType.name ??
            'None',
          { length: 30 }
        ),
      }
    : null;

  // Organization-specific data processing
  const orgTruncatedData = isOrganization
    ? {
        displayName: truncate(data.displayName, { length: 35 }),
        membershipTypeName: truncate(
          data.membershipData?.currentMembershipTerm?.membershipType?.name ??
            'None',
          { length: 25 }
        ),
      }
    : null;

  const getName = () => {
    if (isMember) {
      return memberTruncatedData?.displayName || data.email;
    }
    return orgTruncatedData?.displayName || data.name;
  };

  const getContact = () => {
    if (isMember) return data.primaryPhone || '-';

    return organizationAttributes?.phone || data.phone || '-';
  };

  const getEmail = () => {
    if (isMember) return memberTruncatedData?.email || data.email || '-';

    return (
      organizationAttributes?.email ||
      data.email ||
      data.accountOwners?.[0]?.email ||
      '-'
    );
  };
  const getMembershipStatus = () => {
    // Wait for display config so Past Due / Ex-Member don't flash then hide
    if (displayConfigLoading) {
      return null;
    }
    if (memberManagement.isActive && data.membershipData?.membershipStatus) {
      // Switch on: Ex-Member → Non-Member; switch off: keep original status
      const membershipStatus = getDisplayMembershipStatus(
        data.membershipData.membershipStatus,
        showActiveAndNonMembersOnly
      ) as keyof typeof MEMBERSHIP_STATUS_COLOR;
      return (
        <VStack spacing={1}>
          <Badge
            color={MEMBERSHIP_STATUS_COLOR[membershipStatus]}
            bg={MEMBERSHIP_STATUS_COLOR[membershipStatus] + '10'}
            variant="subtle"
            px={2}
            py={1}
            borderRadius="md"
            fontWeight="medium"
            fontSize="10px"
          >
            {READABLE_MEMBERSHIP_STATUS[membershipStatus]}
          </Badge>
          {/* Switch on: hide ALL extra alerts — only Active Member / Non-Member */}
          {!showActiveAndNonMembersOnly &&
            data.membershipData?.mostRelevantMembershipTermAlert && (
            <MostRelevantMembershipTermAlertTag
              smallbadge={true}
              mostRelevantMembershipTermAlert={
                data.membershipData.mostRelevantMembershipTermAlert
              }
            />
          )}
        </VStack>
      );
    }

    if (isMember) {
      return (
        <Badge
          color={data.integrationIsMember ? '#04AD04' : '#B87E00'}
          bg={
            data.integrationIsMember
              ? 'rgba(4, 173, 4, 0.1)'
              : 'rgba(184, 126, 0, 0.1)'
          }
          variant="subtle"
          px={2}
          py={1}
          borderRadius="md"
          fontWeight="medium"
          fontSize="10px"
        >
          {data.integrationIsMember ? 'Member' : 'Non-Member'}
        </Badge>
      );
    }

    if (!memberManagement.isActive) {
      return (
        <Badge
          color={data.isMember ? '#04AD04' : '#B87E00'}
          bg={
            data.isMember
              ? 'rgba(4, 173, 4, 0.1)'
              : 'rgba(184, 126, 0, 0.1)'
          }
          variant="subtle"
          px={2}
          py={1}
          borderRadius="md"
          fontWeight="medium"
          fontSize="10px"
        >
          {data.isMember ? 'Member' : 'Non-Member'}
        </Badge>
      );
    }

    return '—';
  };

  const getMembershipType = () => {
    if (memberManagement.isActive) {
      const typeName = isMember
        ? memberTruncatedData?.membershipTypeName
        : orgTruncatedData?.membershipTypeName;

      if (typeName && typeName !== 'None') {
        return (
          <Tooltip
            label={
              isMember
                ? data.membershipData?.currentMembershipTerm?.membershipType
                    .name ?? 'None'
                : data.membershipData?.currentMembershipTerm?.membershipType
                    ?.name ?? 'None'
            }
          >
            <Text fontSize="sm">{typeName}</Text>
          </Tooltip>
        );
      }
    }

    return <Text fontSize="sm">None</Text>;
  };

  const getAccountStatus = () => {
    if (isMember) {
      return (
        <AccountStatus
          status={data.accountStatus}
          deactivated={!!data.deactivated}
        />
      );
    }
    return (
      <Badge
        color={data.hidden ? '#B87E00' : '#04AD04'}
        bg={data.hidden ? 'rgba(184, 126, 0, 0.1)' : 'rgba(4, 173, 4, 0.1)'}
        variant="subtle"
        px={2}
        py={1}
        borderRadius="md"
        fontWeight="medium"
        fontSize="10px"
      >
        {data.hidden ? 'Hidden' : 'Active'}
      </Badge>
    );
  };

  const getRole = () => {
    if (isMember) {
      return (
        <Role role={data.role} hasAccount={!!data.hasAccount} towLine={true} />
      );
    }
    return <Text fontSize="sm">{data.isSponsor ? 'Sponsor' : 'Record'}</Text>;
  };

  const handleRowClick = () => {
    if (isMember) {
      const contactsDetailBasePath = archivedOnly
        ? '/admin/membership/archived-users'
        : '/admin/membership/people';
      router.push(`${contactsDetailBasePath}/${data._id}`);
    } else {
      const orgDetailsBasePath = getOrganizationAdminBasePath({
        organizationId: data._id,
        archivedOnly,
      });
      router.push(`${orgDetailsBasePath}/details`);
    }
  };

  const useNewContactDesign = data.switchToNewContactDesign ?? false;

  const renderExpandedContent = () => {
    if (isMember) {
      return (
        <Flex
          p={3}
          bg="secondary.500"
          direction="row"
          align="stretch"
          gap={3}
          w="full"
        >
          {/* Member Contact Info Section */}
          <HStack
            spacing={4}
            bg="white"
            p="4"
            borderRadius="10px"
            minH="full"
            align="stretch"
            w="50%"
          >
            <VStack align="start" spacing={4} w="full">
              <Text textStyle="xlBodyStrong">Contact Info</Text>
              <HStack
                spacing={6}
                alignItems="center"
                justifyContent="space-between"
                w="full"
                mb="3"
              >
                <HStack>
                  <Text color="primary.500">
                    <BrifcaseIcon mt="-8px" boxSize="5" />
                  </Text>
                  <Text fontWeight="medium">
                    {data.company || (
                      <Text
                        as="span"
                        fontSize="sm"
                        color="gray.500"
                        fontStyle="italic"
                      >
                        No organization info
                      </Text>
                    )}
                  </Text>
                </HStack>
                <HStack>
                  <Text color="primary.500">
                    <PhoneNoIcon boxSize="18px" overflow="visible" mt="-8px" />
                  </Text>
                  <Text fontWeight="medium">
                    {data.primaryPhone || (
                      <Text
                        as="span"
                        fontSize="sm"
                        color="gray.500"
                        fontStyle="italic"
                      >
                        No phone info
                      </Text>
                    )}
                  </Text>
                </HStack>
                <HStack overflow="hidden">
                  <Text color="primary.500">
                    <MapPinIcon boxSize="18px" />
                  </Text>
                  <Text fontWeight="medium">
                    {[
                      data.addressLocality,
                      data.addressAdministrativeArea,
                    ].some(Boolean) ? (
                      [data.addressLocality, data.addressAdministrativeArea]
                        .filter(Boolean)
                        .join(', ')
                    ) : (
                      <Text
                        as="span"
                        fontSize="sm"
                        color="gray.500"
                        fontStyle="italic"
                      >
                        No address info
                      </Text>
                    )}
                  </Text>
                </HStack>
              </HStack>

              <HStack
                spacing={8}
                bg="secondary.500"
                p="4"
                w="full"
                alignItems="center"
                justifyContent="space-between"
                borderRadius="10px"
              >
                {isLoadingMemberDetail ? (
                  <>
                    {[0, 1, 2, 3].map((i) => (
                      <VStack key={i} spacing={2} alignItems="center" w="full">
                        <Skeleton height="32px" width="28px" />
                        <Skeleton height="14px" width="56px" />
                      </VStack>
                    ))}
                  </>
                ) : (
                <>
                {useNewContactDesign && (
                  <>
                    {postCount > 0 ? (
                      <Link
                        as={NextLink}
                        href={`/admin/membership/people/${data._id}#postsCard`}
                        _hover={{ textDecoration: 'none' }}
                        w="full"
                      >
                        <VStack
                          spacing={0}
                          alignItems="center"
                          justifyContent="space-between"
                          w="full"
                        >
                          <Text
                            fontWeight="bold"
                            fontSize="2xl"
                            color="primary.500"
                            mb="2"
                          >
                            {postCount}
                          </Text>
                          <Text
                            fontSize="sm"
                            color="textSecondary.500"
                            fontWeight="medium"
                          >
                            Posts
                          </Text>
                        </VStack>
                      </Link>
                    ) : (
                      <VStack
                        spacing={0}
                        alignItems="center"
                        justifyContent="space-between"
                        w="full"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="2xl"
                          color="primary.500"
                          mb="2"
                        >
                          {postCount}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="textSecondary.500"
                          fontWeight="medium"
                        >
                          Posts
                        </Text>
                      </VStack>
                    )}

                    {likeCount > 0 ? (
                      <Link
                        as={NextLink}
                        href={`/admin/membership/people/${data._id}#LikesCard`}
                        _hover={{ textDecoration: 'none' }}
                        w="full"
                      >
                        <VStack
                          spacing={0}
                          alignItems="center"
                          justifyContent="space-between"
                          w="full"
                        >
                          <Text
                            fontWeight="bold"
                            fontSize="2xl"
                            color="primary.500"
                            mb="2"
                          >
                            {likeCount}
                          </Text>
                          <Text
                            fontSize="sm"
                            color="textSecondary.500"
                            fontWeight="medium"
                          >
                            Likes
                          </Text>
                        </VStack>
                      </Link>
                    ) : (
                      <VStack
                        spacing={0}
                        alignItems="center"
                        justifyContent="space-between"
                        w="full"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="2xl"
                          color="primary.500"
                          mb="2"
                        >
                          {likeCount}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="textSecondary.500"
                          fontWeight="medium"
                        >
                          Likes
                        </Text>
                      </VStack>
                    )}

                    {commentCount > 0 ? (
                      <Link
                        as={NextLink}
                        href={`/admin/membership/people/${data._id}#CommentsCard`}
                        _hover={{ textDecoration: 'none' }}
                        w="full"
                      >
                        <VStack
                          spacing={0}
                          alignItems="center"
                          justifyContent="space-between"
                          w="full"
                        >
                          <Text
                            fontWeight="bold"
                            fontSize="2xl"
                            color="primary.500"
                            mb="2"
                          >
                            {commentCount}
                          </Text>
                          <Text
                            fontSize="sm"
                            color="textSecondary.500"
                            fontWeight="medium"
                          >
                            Comments
                          </Text>
                        </VStack>
                      </Link>
                    ) : (
                      <VStack
                        spacing={0}
                        alignItems="center"
                        justifyContent="space-between"
                        w="full"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="2xl"
                          color="primary.500"
                          mb="2"
                        >
                          {commentCount}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="textSecondary.500"
                          fontWeight="medium"
                        >
                          Comments
                        </Text>
                      </VStack>
                    )}
                  </>
                )}

                {!useNewContactDesign && (
                  <>
                    <VStack
                      spacing={0}
                      alignItems="center"
                      justifyContent="space-between"
                      w="full"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="2xl"
                        color="primary.500"
                        mb="2"
                      >
                        {postCount}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="textSecondary.500"
                        fontWeight="medium"
                      >
                        Posts
                      </Text>
                    </VStack>

                    <VStack
                      spacing={0}
                      alignItems="center"
                      justifyContent="space-between"
                      w="full"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="2xl"
                        color="primary.500"
                        mb="2"
                      >
                        {likeCount}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="textSecondary.500"
                        fontWeight="medium"
                      >
                        Likes
                      </Text>
                    </VStack>

                    <VStack
                      spacing={0}
                      alignItems="center"
                      justifyContent="space-between"
                      w="full"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="2xl"
                        color="primary.500"
                        mb="2"
                      >
                        {commentCount}
                      </Text>
                      <Text
                        fontSize="sm"
                        color="textSecondary.500"
                        fontWeight="medium"
                      >
                        Comments
                      </Text>
                    </VStack>
                  </>
                )}
                <VStack
                  spacing={0}
                  alignItems="center"
                  justifyContent="space-between"
                  w="full"
                >
                  <Text
                    fontWeight="bold"
                    fontSize="2xl"
                    color="primary.500"
                    mb="2"
                  >
                    {badgeCount}
                  </Text>
                  <Text
                    fontSize="sm"
                    color="textSecondary.500"
                    fontWeight="medium"
                  >
                    Badges
                  </Text>
                </VStack>
                </>
                )}
              </HStack>
            </VStack>
          </HStack>

          {/* Member Membership Section */}
          {memberManagement.isActive && (
            <Box w="50%">
              {isLoadingMemberDetail ? (
                <Flex
                  bg="white"
                  p="4"
                  minH="184px"
                  borderRadius="10px"
                  align="center"
                  justify="center"
                  h="full"
                >
                  <Text color="gray.500" fontSize="sm">
                    Loading membership...
                  </Text>
                </Flex>
              ) : memberMembershipData && memberDetailUser ? (
                <AccordionMembershipCard
                  userId={memberDetailUser._id}
                  userName={memberDetailUser.firstName}
                  membershipData={memberMembershipData as any}
                  displayName={memberDetailUser.displayName}
                  creditBalance={
                    memberDetailUser.creditBalance
                      ? dinero(memberDetailUser.creditBalance)
                      : dinero({ amount: 0, currency: USD })
                  }
                  paymentMethods={memberDetailUser.paymentMethods?.map(
                    (method) => ({
                      _id: method._id,
                      isDefault: method.isDefault,
                    })
                  )}
                  shouldBeAdminView
                  variant="new"
                  userDeactivated={!!data.deactivated}
                />
              ) : (
                <Flex
                  bg="white"
                  p="4"
                  borderRadius="10px"
                  align="center"
                  justify="center"
                  h="full"
                >
                  <Text color="gray.500" fontSize="sm">
                    No membership data
                  </Text>
                </Flex>
              )}
            </Box>
          )}

          {!memberManagement.isActive &&  (
            <MembershipEmptyCard integrationIsMember={data.integrationIsMember} accountStatus={data.accountStatus} deactivated={!!data.deactivated}  individualView={true}/>
          )}
        </Flex>
      );
    } else {
      // Organization expanded content - exact match to OrganizationRow
      const attributes = organizationAttributes || {
        email: '-',
        phone: '-',
        website: '-',
        location: '-',
      };

      const summaryEmail =
        attributes.email || data.accountOwners?.[0]?.email || '-';
      const membershipStatus = data.membershipData
        ?.membershipStatus as keyof typeof MEMBERSHIP_STATUS_COLOR;

      return (
        <Flex
          p={3}
          bg="secondary.500"
          direction="row"
          align="stretch"
          gap={3}
          w="full"
        >
          <HStack spacing={4} bg="white" p="0" borderRadius="10px" w="50%">
            <VStack align="start" spacing={4} w="full">
              <Card w="full">
                <Box position="relative">
                  <Image
                    w="full"
                    maxH="130px"
                    borderTopRadius="xl"
                    src={orgBannerImage?.imageProxy || undefined}
                    style={{ objectFit: 'cover' }}
                    fallback={
                      <Stack
                        bg="#D9D9D9"
                        height="130px"
                        w="full"
                        borderTopLeftRadius="10px"
                        borderTopRightRadius="10px"
                      ></Stack>
                    }
                    alt="company banner"
                  />

                  <BannerImageMenu
                    hasBannerImage={!!orgBannerImage?.imageProxy}
                    allowRemove={true}
                    updateBannerImage={getUpdate('banner')}
                    isLoading={isUpdatingImage}
                    iconView={true}
                  />
                </Box>

                <Stack direction="row" p="3">
                  <OrganizationProfileImage
                    mt="-12"
                    mr="2"
                    organization={data}
                    flexShrink="0"
                    w="100px"
                    h="90px"
                  >
                    <ProfileImageMenu
                      hasProfileImage={!!data.profilePicture?.imageProxy}
                      updateProfileImage={getUpdate('profile')}
                      isLoading={isUpdatingImage}
                      objectFit="contain"
                      iconView={true}
                    />
                  </OrganizationProfileImage>
                  <Stack spacing="1" width="full">
                    <Stack spacing={2} w="full" minW="100%">
                      <Flex
                        justify="space-between"
                        align="stretch"
                        h="full"
                        w="full"
                      >
                        <HStack spacing={2} align="center" w="100%" h="full">
                          <Heading
                            size="sm"
                            noOfLines={2}
                            whiteSpace="normal"
                            fontFamily="Roboto, sans-serif"
                          >
                            {data.displayName}
                          </Heading>
                          {data.membershipData?.membershipStatus && (
                            <Badge
                              color={MEMBERSHIP_STATUS_COLOR[membershipStatus]}
                              bg={
                                MEMBERSHIP_STATUS_COLOR[membershipStatus] + '10'
                              }
                              variant="subtle"
                              px={2}
                              py={1}
                              borderRadius="md"
                              fontWeight="medium"
                              fontSize="10px"
                            >
                              {READABLE_MEMBERSHIP_STATUS[membershipStatus]}
                            </Badge>
                          )}
                        </HStack>

                        <Text color="primary.500" cursor="pointer">
                          <Menu placement="bottom-end">
                            <MenuButton
                              size="sm"
                              as={IconButton}
                              variant="outline"
                              colorScheme="primary"
                              bg="transparent"
                              borderColor="transparent"
                              ml="1"
                              icon={<MoreVerticalIcon color="primary.500" />}
                            />
                            <MenuList>
                              <MenuItem
                                onClick={orgEditModal.onOpen}
                                color="textSecondary.500"
                              >
                                <EditIcon
                                  boxSize={5}
                                  mr={2}
                                  color="primary.500"
                                />{' '}
                                Edit Info
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Text>
                      </Flex>

                      <SimpleGrid
                        columns={{ base: 1, md: 2 }}
                        spacingX={2}
                        spacingY={2}
                      >
                        <HStack spacing={2} align="center" minW="50%">
                          <Text color="primary.500">
                            <EmailIcon
                              boxSize="18px"
                              overflow="visible"
                              mt="-8px"
                            />
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            color="gray.700"
                            wordBreak="break-word"
                            whiteSpace="pre-wrap"
                          >
                            {attributes.email || '-'}
                          </Text>
                        </HStack>
                        <HStack spacing={2} align="center" minW="50%">
                          <Text color="primary.500">
                            <PhoneNoIcon
                              boxSize="18px"
                              overflow="visible"
                              mt="-5px"
                            />
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            color="gray.700"
                            whiteSpace="nowrap"
                          >
                            {attributes.phone || '-'}
                          </Text>
                        </HStack>
                        <HStack spacing={2} align="center">
                          <Text color="primary.500">
                            <GlobeIcon
                              boxSize="18px"
                              overflow="visible"
                              mt="-5px"
                            />
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            color="gray.700"
                            wordBreak="break-word"
                            whiteSpace="pre-wrap"
                          >
                            {attributes.website || '-'}
                          </Text>
                        </HStack>
                        <HStack spacing={2} align="center">
                          <Text color="primary.500">
                            <MapPinIcon boxSize="18px" mt="-4px" />
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            color="gray.700"
                            whiteSpace="normal"
                          >
                            {attributes.location || '-'}
                          </Text>
                        </HStack>
                      </SimpleGrid>
                    </Stack>
                  </Stack>
                </Stack>
              </Card>
            </VStack>
          </HStack>

          {/* Organization Membership Section */}
          <Box w="50%" display="flex">
            {memberManagement.isActive && (
              <Box w="50%" display="flex" h="full" flex="1">
                {isLoadingOrgDetail ? (
                  <Flex
                    bg="white"
                    p="4"
                    minH="184px"
                    borderRadius="10px"
                    align="center"
                    justify="center"
                    h="full"
                    flex="1"
                  >
                    <Text color="gray.500" fontSize="sm">
                      Loading membership...
                    </Text>
                  </Flex>
                ) : orgMembershipData ? (
                  <AccordionMembershipCard
                    organizationId={data._id}
                    membershipData={orgMembershipData as any}
                    displayName={data.displayName}
                    creditBalance={
                      orgDetailResult?.creditBalance
                        ? dinero(orgDetailResult.creditBalance)
                        : dinero({ amount: 0, currency })
                    }
                    organizationIsOwnerless={
                      !(orgDetailResult?.accountOwners?.length ??
                        data.accountOwners?.length)
                    }
                    shouldBeAdminView
                    variant="new"
                  />
                ) : (
                  <Flex
                    bg="white"
                    p="4"
                    borderRadius="10px"
                    align="center"
                    justify="center"
                    h="full"
                    minH="full"
                    flex="1"
                  >
                    <Text color="gray.500" fontSize="sm">
                      No membership data
                    </Text>
                  </Flex>
                )}
              </Box>
            )}

          {!memberManagement.isActive &&  (
            <MembershipEmptyCard organizationView={true} hidden={data?.hidden}
             isMember={data?.isMember} isSponsor={data?.isSponsor} />
          )}
          </Box>
        </Flex>
      );
    }
  };

  return (
    <>
      <Tr _hover={{ bg: 'bgPrimary.500' }}>
        <Td pr="2" pl="2" width={10}>
          <IconButton
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            size="md"
            variant="ghost"
            p={0}
            minW="auto"
            minH="auto"
            h="auto"
            w="auto"
            mr="2"
            mt="-2"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
          />
        </Td>

        <Td
          pr="2"
          pl="2"
          _hover={{ cursor: 'pointer' }}
          onClick={handleRowClick}
        >
          <Stack direction="row" spacing="2" alignItems="center" w="full">
            <Avatar
              size="md"
              name={getName()}
              src={
                isMember
                  ? data.proxyProfilePictureUrl
                  : data.profilePicture?.imageProxy
              }
            />
            <Stack spacing="0">
              <Tooltip label={getName()}>
                <Text textStyle="compactStrong" noOfLines={1}>
                  <span>{getName()}</span>
                  {isMember ? (
                    <MemberIcon color="primary.500" boxSize="18px" ml="1" />
                  ) : (
                    <OrganiizationIcon
                      color="primary.500"
                      boxSize="20px"
                      ml="1"
                    />
                  )}
                </Text>
              </Tooltip>
              <Tooltip label={getEmail()}>
                <Text as="span" fontSize="sm">
                  {getEmail()}
                </Text>
              </Tooltip>
            </Stack>
          </Stack>
        </Td>

        <Td pr="2" pl="2">
          <Text textStyle="compact">{getContact()}</Text>
        </Td>

        <Td pr="2" pl="2">
          {getMembershipStatus()}
        </Td>

        {memberManagement.isActive && (
          <Td pr="2" pl="2">
            {getMembershipType()}
          </Td>
        )}

        <Td pr="2" pl="2">
          {getAccountStatus()}
        </Td>

        <Td pr="2" pl="2">
          {getRole()}
        </Td>

        <Td
          pr="2"
          pl="2"
          w="60px"
          minW="60px"
          maxW="60px"
          boxShadow="-8px 0 12px -12px rgba(0,0,0,0.35)"
          position="sticky"
          right={0}
          zIndex={3}
          bg="white"
        >
          <Menu placement="bottom-end" strategy="fixed">
            <MenuButton
              size="sm"
              as={IconButton}
              variant="outline"
              colorScheme="primary"
              bg="transparent"
              borderColor="transparent"
              aria-label="actions"
              ml="8px"
              icon={<MoreHorizontalIcon transform="rotate(90deg)" />}
              onClick={(e) => e.stopPropagation()}
            />
            <Portal>
              <MenuList zIndex="popover">
                <MenuItem
                  fontSize="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isMember) {
                      memberEditModal.onOpen();
                    } else {
                      orgEditModal.onOpen();
                    }
                  }}
                >
                  <EditIcon boxSize={5} mr={2} color="primary.500" />
                  Edit
                </MenuItem>
                <MenuItem
                  fontSize="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowClick();
                  }}
                >
                  <EyeIcon boxSize={5} mr={2} color="primary.500" />
                  View
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Td>
      </Tr>

      {/* Expandable Row */}
      <Tr>
        <Td
          colSpan={memberManagement.isActive ? 8 : 7}
          p={0}
          borderBottom={isExpanded ? '1px' : 'none'}
          borderBottomColor="strokePrimary.500"
          w="full"
        >
          <Collapse in={isExpanded} animateOpacity>
            {renderExpandedContent()}
          </Collapse>
        </Td>
      </Tr>

      {/* Edit Modals */}
      {isMember && (
        <EditMemberModal
          isOpen={memberEditModal.isOpen}
          onClose={memberEditModal.onClose}
          member={data}
        />
      )}

      {isOrganization && orgEditModal.isOpen && (
        <CompanyInfoEdit
          organization={data as unknown as CompanyInfoEdit_OrganizationFragment}
          isOpen={orgEditModal.isOpen}
          onClose={orgEditModal.onClose}
        />
      )}
    </>
  );
};
